import React, { useState, useEffect } from 'react';
import { BlueGovLayout } from '../layouts/BlueGovLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Building2, FileText, Users, DollarSign, TrendingUp, AlertTriangle,
  ArrowUpRight, ArrowRight, Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const BlueGovDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ clients: 0, contracts: 0, mrr: 0, seats: 0 });
  const [recentClients, setRecentClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('bluegov_token');
    if (!token) return;

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [clientsRes, contractsRes, licensesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bluegov-admin/clients`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bluegov-admin/contracts`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bluegov-admin/licenses/summary`, { headers })
      ]);

      const clients = await clientsRes.json();
      const contracts = await contractsRes.json();
      const licenses = await licensesRes.json();

      const activeClients = Array.isArray(clients) ? clients.filter(c => c.status === 'ATIVO').length : 0;
      const totalMRR = Array.isArray(contracts) ? contracts.filter(c => c.status === 'VIGENTE').reduce((sum, c) => sum + (c.monthly_value || 0), 0) : 0;

      setStats({
        clients: activeClients,
        contracts: Array.isArray(contracts) ? contracts.length : 0,
        mrr: totalMRR,
        seats: licenses.total_seats_contracted || 0
      });

      setRecentClients(Array.isArray(clients) ? clients.slice(0, 5) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BlueGovLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Dashboard</h1>
            <p className="text-slate-400 mt-1">Visão geral do negócio BlueGov</p>
          </div>
          <Button onClick={() => navigate('/bluegov/clients')} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Novo Cliente
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'MRR Total', value: `R$ ${stats.mrr.toLocaleString('pt-BR')}`, icon: DollarSign, color: 'emerald', change: '+12%' },
            { label: 'Clientes Ativos', value: stats.clients, icon: Building2, color: 'blue', change: '+3' },
            { label: 'Contratos', value: stats.contracts, icon: FileText, color: 'purple', change: '+2' },
            { label: 'Assentos', value: stats.seats, icon: Users, color: 'amber', change: '+50' }
          ].map((stat, i) => (
            <Card key={i} className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className={`w-12 h-12 rounded-xl bg-${stat.color}-500/20 flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 text-${stat.color}-400`} />
                  </div>
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                    <ArrowUpRight className="w-3 h-3 mr-1" />
                    {stat.change}
                  </Badge>
                </div>
                <div className="mt-4">
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Clients */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Clientes Recentes</CardTitle>
            <Button variant="ghost" className="text-blue-400 hover:text-blue-300" onClick={() => navigate('/bluegov/clients')}>
              Ver todos
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentClients.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum cliente cadastrado</p>
                <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/bluegov/clients')}>
                  Cadastrar Primeiro Cliente
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentClients.map((client) => (
                  <div key={client.client_id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                        {client.uf}
                      </div>
                      <div>
                        <p className="font-medium text-white">{client.name}</p>
                        <p className="text-sm text-slate-400">{client.type} • {client.uf}</p>
                      </div>
                    </div>
                    <Badge className={
                      client.status === 'ATIVO' ? 'bg-emerald-500/20 text-emerald-400 border-0' :
                      client.status === 'EM_IMPLANTACAO' ? 'bg-blue-500/20 text-blue-400 border-0' :
                      'bg-slate-500/20 text-slate-400 border-0'
                    }>
                      {client.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BlueGovLayout>
  );
};

export default BlueGovDashboardPage;
